import { useContext, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useOutletContext } from "react-router";
import { useSearchParams } from "react-router-dom";
import AppContext from "../../../AppContext";
import { APP_HOMEPAGE_ROUTE, APP_TITLE } from "../../../config/constant";
import { defaultStatusColors } from "../../../utils/CardUtils";
import { getDropdownData } from "../../../utils/dropdownData";
import { formatDefaultDateTime } from "../../../utils/Formatter";
import * as Library from "../../../utils/Library";
import { verifyUserIsOnboarding } from "../../../utils/onboardingHelper";
import DialogBoxComponent from "../../common/dialogs/DialogBoxComponent";
import FormCard from "../../common/forms/FormCard";
import FormField, { defaultPlaceholderTextFunction } from "../../common/forms/FormField";
import FormFieldTextAutocomplete from "../../common/forms/FormFieldTextAutocomplete";
import ListComponent from "../../common/lists/ListComponent";
import FullScreenLoader from "../../common/loaders/FullScreenLoader";
import SimpleButtonContentHeader from "../../common/mainApp/mainWindowHeaders/SimpleButtonContentHeader";
import FullScreenOverlay from "../../common/overlay/FullScreenOverlay";

export default function LabelBrandsRequestPage() {
    document.title = `${APP_TITLE} - Label Brands Invitations`;

    // #region STATES
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const name = searchParams.get("name");
    const { MainLayoutProps, layoutType } = useOutletContext();
    const navigations = useSelector(state => state.navigationAccesses);
    const [saveButtonEnabled, setSaveButtonEnabled] = useState(false);
    const { setHasUnsavedChanges } = useContext(AppContext);

    const [brandName, setBrandName] = useState("");

    const [openConfirmRequestDialog, setOpenConfirmRequestDialog] = useState(false);

    const [listNumRows, setListNumRows] = useState(10);

    const props = {
        layoutType: layoutType,
        setControlBarLeftBtns: MainLayoutProps?.setControlBarLeftBtns,
        setControlBarCenterBtns: MainLayoutProps?.setControlBarCenterBtns,
        setControlBarRightBtns: MainLayoutProps?.setControlBarRightBtns,
        setFilterHeaderProperties: MainLayoutProps?.setFilterHeaderProperties,
        setRefreshUserData: MainLayoutProps?.setRefreshUserData
    };

    const [bostagesAccountsEmail, setBostagesAccountsEmail] = useState([]);
    const [isEnteredValidEmail, setIsEnteredValidEmail] = useState(false);
    const [isEmailChanged, setIsEmailChanged] = useState(false);
    const [storesList, setStoresList] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [formIsLoading, setFormIsLoading] = useState(false);
    const [countryOptions] = useState(getDropdownData("countryOptions"));
    const [formLabelsPrefillFields, setFormLabelsPrefillFields] = useState({
        contactName: "",
        contactLastName: "",
        email: "",
        country: ""
    });
    const retailsBrandData = JSON.parse(localStorage.getItem("retailBrands") || "[]");
    const formattedRetailBrandOptions = retailsBrandData.map(brand => ({
        value: brand._id,
        name: brand.name,
        selected: true
    }));
    const [fromRetailBrandOption] = useState(formattedRetailBrandOptions);

    const [labelBrandExists, setLabelBrandExists] = useState(false);
    const [labelBrandSentToEntityID, setLabelBrandSentToEntityID] = useState(undefined);
    // #endregion

    // #region METHODS
    const prefillLabelBrandData = async data => {
        if (!data || !data?.value) return;
        const { value } = data;

        const res = await Library.makePostRequest("getLabelBrandData", { labelBrandID: value }, false, null);

        if (res?.data?.status === 200) {
            const labelBrandData = res?.data?.data;

            setBrandName(labelBrandData?.name);

            const { accountOwnerName, accountOwnerLastName, accountOwnerEmail, location } = labelBrandData;
            setFormLabelsPrefillFields({
                contactName: accountOwnerName,
                contactLastName: accountOwnerLastName,
                email: accountOwnerEmail,
                country: location?.countryValue
            });

            // setSelectedTaxCode(taxPreLabel);
            setLabelBrandExists(true);
            setLabelBrandSentToEntityID(value); // setLabelBrandSentToEntityID when a valid retail brand is selected

            setBostagesAccountsEmail(
                labelBrandData?.BOstagesAccounts.map((user, index) => {
                    return {
                        value: user?._id,
                        name: user?.email,
                        firstName: user?.firstName,
                        lastName: user?.lastName
                    };
                })
            );
        }
    };

    const handleLabelsBrandChange = value => {
        setBrandName(value);
        setLabelBrandExists(false);
        setLabelBrandSentToEntityID(undefined); // setLabelBrandSentToEntityID to undefined when the retail brand value changes
        setBostagesAccountsEmail([]);
        //reset the prefill fields
        setFormLabelsPrefillFields({
            contactName: "",
            contactLastName: "",
            email: "",
            country: ""
        });
    };

    const checkFormIsValid = (highlightValidation = false) => {
        const { toStore } = formCardRef.current.getAllFieldValues();
        const allFieldsAreValid = formCardRef.current.allFieldsAreValid(highlightValidation);
        const hasSelectedStores = toStore && toStore.length > 0;
        const isButtonEnabled = allFieldsAreValid && hasSelectedStores;
        setSaveButtonEnabled(isButtonEnabled);
        return isButtonEnabled;
    };

    const handleSaveClicked = async () => {
        setOpenConfirmRequestDialog(false);
        if (!formCardRef) {
            Library.showToastMessage({
                title: "Error",
                message: "FormCard references not found.",
                type: "error"
            });
            return;
        }

        // Check if all fields are valid
        const allFieldsAreValid = checkFormIsValid(true);
        if (!allFieldsAreValid) {
            Library.showToastMessage({
                title: "Error",
                message: "Please verify all fields.",
                type: "error"
            });
            return;
        }

        // Get all field values
        const fieldValues = formCardRef.current.getAllFieldValues();

        setHasUnsavedChanges(false);

        const labelBrandName = fieldValues.brands.name;

        const retailBrandID = fieldValues.fromRetailBrand.value || formattedRetailBrandOptions[0].value;
        const labelBrandID = fieldValues.brands.value || labelBrandSentToEntityID;
        const storeIDs = fieldValues.toStore.map(store => store.value);
        const contactName = fieldValues?.contactLastName
            ? fieldValues.contactName + " " + fieldValues.contactLastName
            : fieldValues.contactName;
        const contactFirstName = fieldValues.contactName;
        const contactLastName = fieldValues.contactLastName;
        const contactEmail = fieldValues.email;
        const country = fieldValues?.country?.value;

        if (!contactName || !contactEmail?.name) {
            Library.showToastMessage({
                title: "Error",
                message: "Contact Name and Email are required.",
                type: "error"
            });
            return;
        }

        const allStores = storesList.length;
        const numberOfStores = storeIDs.length === allStores ? "all" : storeIDs.length;

        const res = await inviteBrand(
            labelBrandName,
            labelBrandID,
            storeIDs,
            contactName,
            contactEmail,
            country,
            contactFirstName,
            contactLastName,
            retailBrandID
        );

        const retailBrandName = localStorage.getItem("currentOrganizationName");

        if (res?.data?.status === 200) {
            Library.showToastMessage({
                title: "Request Sent!",
                //message: `The label brand ${labelBrandName} will get a request by email to accept ${numberOfStores} ${retailBrandName} stores as their fulfillment centers.`,
                message: `${labelBrandName} will receive this request to accept ${retailBrandName} ${numberOfStores} stores as their fulfillment centers.`,
                type: "success"
            });

            const page = verifyUserIsOnboarding(navigations?.navigationAccesses);
            const isOnboarding = page !== APP_HOMEPAGE_ROUTE;
            if (isOnboarding) {
                navigate("/companies/kyc-policy");
            } else {
                navigate("/labelbrands/requests");
            }
        } else {
            setHasUnsavedChanges(true);
        }
    };

    const inviteBrand = async (
        labelBrandName,
        labelBrandID,
        storeIDs,
        contactName,
        contactEmail,
        country,
        contactFirstName,
        contactLastName,
        retailBrandID
    ) => {
        const inviterName = localStorage.getItem("userFirstName");

        const postData = {
            BOstagesAccountName: inviterName,
            retailBrandID,
            newLabelBrandName: !labelBrandID ? labelBrandName : undefined,
            labelBrandID,
            labelBrandContactFirstName: contactFirstName,
            labelBrandContactLastName: contactLastName,
            labelBrandContactEmail: contactEmail?.name,
            //labelBrandSuggestedContactFirstName
            //labelBrandSuggestedContactLastName
            //labelBrandSuggestedContactEmail
            storeIDs
            //isMoreStores
            //forced
        };

        const res = await Library.makePostRequest("inviteLabelBrand", postData, false, setFormIsLoading);
        return res;
    };

    const handleAccountEmailSelect = data => {
        if (data?.value) {
            setIsEnteredValidEmail(false);
            setIsEmailChanged(false);
        }
        setFormLabelsPrefillFields(prev => ({
            ...prev,
            email: data?.name,
            contactName: data?.firstName,
            contactLastName: data?.lastName
        }));
    };

    const handleAccountEmailChange = email => {
        setIsEnteredValidEmail(/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email));
        setIsEmailChanged(true);
        setFormLabelsPrefillFields(prev => ({
            ...prev,
            email: email
        }));
    };
    // #endregion

    // #region FORM FIELDS
    const formCardRef = useRef();
    const formFields1Refs = [];

    const FormCardFields1 = [
        <FormField
            type="dropdown"
            title="From Retail Brand"
            tooltip="Enter the Retail Brand you want to send the invite from. This is your Brand. If you only have one brand enrolled, the filed will be locked"
            dropdownData={fromRetailBrandOption}
            varName="fromRetailBrand"
            isDropdownMultiselect={false}
            isDropdownSearchable={true}
            requiredType="locked"
            ref={el => (formFields1Refs.fromRetailBrand = el)}
            assistiveText=" "
        />,
        <FormFieldTextAutocomplete
            type="text"
            title="To Label Brand"
            tooltip="Enter the Label brand"
            requiredType="mandatory"
            varName="brands"
            autoCompleteAPIData={{
                apiName: "searchLabelBrandsForFFC",
                apiSearchFieldName: "labelBrandName",
                returnArrayName: "data",
                returnFieldIDName: "_id",
                returnFieldValueName: "name",
                customMapping: data => {
                    return data?.map(i => {
                        let name = i?.name;
                        if (i?.location?.city) name += `, ${i?.location?.city}`;
                        if (i?.location?.countryValue) name += `, ${i?.location?.countryValue}`;

                        return { name: name, value: i?._id };
                    });
                }
            }}
            value={name}
            ref={el => (formFields1Refs.brands = el)}
            itemNotSelectedWillClear={false}
            noSearchResultsHideMessage={true}
            onSelectedItemChange={prefillLabelBrandData}
            onTextChange={v => handleLabelsBrandChange(v)}
            assistiveText=" "
        />,
        <FormField
            type="dropdown"
            title="Stores"
            tooltip="Enter the Store"
            dropdownData={storesList}
            requiredType="mandatory"
            varName="toStore"
            ref={el => (formFields1Refs.toStore = el)}
            isDropdownMultiselect={true}
            isDropdownSearchable={true}
            multidropdownOptions={{
                useDoneButton: true,
                useSelectAll: true,
                selectAllText: "Select all stores",
                placeholderTextFunction: defaultPlaceholderTextFunction,
                allItems: storesList,
                labelAllSelected: "All stores",
                labelNoneSelected: "Select stores"
            }}
            useDropdownPlaceholderAsTag={true}
            assistiveText=" "
        />,
        <FormField
            type="text"
            title="Contact first name"
            tooltip="Enter the Contact First Name"
            varName="contactName"
            ref={el => (formFields1Refs.contactName = el)}
            value={formLabelsPrefillFields.contactName}
            //useValueMasking={formLabelsPrefillFields.contactName ? (labelBrandExists ? true : false) : false}
            requiredType={"mandatory"}
        />,
        <FormField
            type="text"
            title="Last name"
            tooltip="Enter the Contact Last Name"
            varName="contactLastName"
            ref={el => (formFields1Refs.contactLastName = el)}
            value={formLabelsPrefillFields?.contactLastName}
            assistiveText=" "
        />,
        <FormFieldTextAutocomplete
            type="email"
            title="Email"
            tooltip="Enter the email address to send the invitation to. The invitation will be addressed to the contact person provided in the previous field. If the retail brand is already in the BOtag network, this field will be locked."
            requiredType={"mandatory"}
            autoCompleteStaticData={bostagesAccountsEmail}
            varName="email"
            ref={el => (formFields1Refs.email = el)}
            itemNotSelectedWillClear={false}
            noSearchResultsHideMessage={true}
            value={formLabelsPrefillFields.email}
            onSelectedItemChange={handleAccountEmailSelect}
            onTextChange={handleAccountEmailChange}
            assistiveText=" "
        />
    ];
    // #endregion

    // #region EFFECTS
    useEffect(() => {
        setupHeaders(props, navigations, saveButtonEnabled, () => setOpenConfirmRequestDialog(true));
    }, [props?.layoutType, saveButtonEnabled]);

    useEffect(() => {
        const getStores = async () => {
            const res = await Library.makeCommonPostRequest(
                "Stores",
                "getStoresData",
                {
                    companyID: localStorage.getItem("currentCompanyID"),
                    page: 1,
                    limit: 1000
                },
                false,
                setIsLoading
            );
            const stores = res?.data?.data?.result ?? [];

            const mappedStores = stores.map(store => ({ value: store.id, name: store.name }));
            setStoresList(mappedStores);
        };
        getStores();
    }, []);
    // #endregion

    // #region LIST COMPONENT PROPS
    const listColumns = [
        {
            name: "Request sent to",
            selector: row => row?.labelBrand?.name ?? "--"
        },
        {
            name: "BOtag ID",
            selector: row => row?.ourID ?? "--"
        },
        {
            name: "Requested on",
            selector: row => formatDefaultDateTime(row?.createdAt) ?? "--"
        },
        {
            name: "Accepted on",
            selector: row => (row?.statusLog ? formatDefaultDateTime(row?.statusLog[0]?.date) : "--")
        },
        {
            name: "Contact person",
            selector: row => (row?.status === "Accepted" ? row?.contactFirstName : (row?.contactFirstName ?? "--"))
        },
        {
            name: "Contact email",
            selector: row => (row?.status === "Accepted" ? row?.contactEmail : (row?.contactEmail ?? "--"))
        },
        {
            name: "Requested stores",
            selector: row => row?.requestedStores ?? "--"
        },
        {
            name: "Accepted stores",
            selector: row => row?.acceptedStores ?? "--"
        },
        {
            name: "To brand",
            selector: row => row?.retailBrand?.name ?? "--"
        }
    ];
    // #endregion

    // #region RENDER
    return (
        <>
            <FullScreenLoader isLoading={isLoading || formIsLoading} />

            <FullScreenOverlay show={openConfirmRequestDialog}>
                <DialogBoxComponent
                    title="Ready to request?"
                    message={`Ready to request ${brandName} to accept your stores as their fulfillment centers?`}
                    showCancel={true}
                    heading="JUST CHECKING..."
                    headingColor="var(--text-icons-blue-500)"
                    onClickYes={() => {
                        handleSaveClicked();
                    }}
                    onClickNo={() => {
                        setOpenConfirmRequestDialog(false);
                    }}
                />
            </FullScreenOverlay>

            <FormCard
                ref={formCardRef}
                title="SEND REQUEST TO A LABEL BRAND"
                numOfFieldsPerLine={4}
                showDefaultRightLegends={true}
                formFields={FormCardFields1}
                formFieldRefs={formFields1Refs}
                onFormChanged={checkFormIsValid}
                showUnsavedChangesPopup={true}
                setIsloading={formIsLoading}
            />

            <ListComponent
                title="Requests"
                title2="(Last 10)"
                columns={listColumns}
                api={{
                    endpoint: "getRequestsToLabelBrands",
                    formData: {
                        companyID: localStorage.getItem("currentCompanyID"),
                        page: 1,
                        limit: listNumRows,
                        includeStatusAccepted: true
                    }
                    // onSuccess: mapListData
                }}
                showOnlyLast10={true}
                showLoader={false}
                pageLimit={listNumRows}
                currentPage={1}
                queryKey={"labelBrandsRequestsList"}
                actions={{
                    includeActions: false, //TODO: Check if is to add actions on request page or only on requests
                    editIsVisible: false,
                    suspendIsVisible: false,
                    removeIsVisible: false,
                    extraActions: [
                        {
                            title: "Remove",
                            onClick: undefined
                        },
                        {
                            title: "Resend"
                            // onClick: row => {
                            //     if (row?.status !== "Request" && row?.status !== "Invited") return undefined;
                            //     handleAcceptInvitation(row?._id);
                            // },
                            // isDisabled: row => {
                            //     return row?.status !== "Request" && row?.status !== "Invited";
                            // }
                        }
                    ]
                }}
                includeStatus={{
                    stickyStatus: true,
                    statusFromPath: "status",
                    statusColors: defaultStatusColors
                }}
                sortByCreatedAt={true}
                onChangeRowsPerPage={rowsPerPage => setListNumRows(rowsPerPage)}
            />
        </>
    );
    // #endregion
}

// #region HELPER FUNCTIONS
function setupHeaders(props, navigations, saveButtonEnabled, onBtnClicked) {
    const createMode = navigations?.navigationAccesses?.fulfillmentCenters?.createModeOnly;

    props.setControlBarLeftBtns([{ to: "#", disabled: true, title: "FFCenters", icon: "fa-magnifying-glass" }]);

    props.setControlBarCenterBtns([
        { to: "/labelbrands/connections", active: false, disabled: createMode, title: "CONNECTIONS" },
        { to: "/labelbrands/invitations", active: false, disabled: false, title: "INVITATIONS" },
        { to: "/labelbrands/requests", active: true, disabled: false, title: "REQUESTS" }
    ]);
    props.setControlBarRightBtns([
        { to: "/labelbrands/requests", active: false, disabled: createMode, title: "View", icon: "eye" },
        { to: "#", active: true, disabled: createMode, title: "Request", icon: "mail" },
        { to: "#", active: false, disabled: true, title: "Details", icon: "microscope" }
    ]);

    props.setFilterHeaderProperties({
        customContentHeader: (
            <SimpleButtonContentHeader btnTitle="REQUEST" btnEnabled={saveButtonEnabled} onClick={onBtnClicked} />
        )
    });
}
// #endregion
